import * as React from 'react'
import { castingStoreDisplayPriceWithTax, StoreProps } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import SelectableListComponent from '../../../shared/selectable-list.component'
import { PrimaryButton } from '../casting-styled.component'
import { currencyFormat, currencyWrapTaxNotice } from '../../../../services/utils/currency.service'
import { useTheme } from 'styled-components'
import { DefaultTheme } from 'styled-components'
import { SupplierBase } from '../../../../services/json-schemas/base/supplier'
import CustomerPlanDescriptionComponent from './customer-plan-description.component'

const i18nScoped = new I18nService(require('./casting-customer-plans.component.yml'))

export default function CastingCustomerPlansComponent ({ store, data: { customer, customerPlan, offeredServices, offeredService, market } }: StoreProps) {
  const theme = useTheme()
  const displayPriceWithTax = React.useMemo(() => castingStoreDisplayPriceWithTax({ customer, offeredService, market }), [customer, market, offeredService])

  const skipCustomerPlan = React.useCallback(() => {
    store.set({ customerPlan: null })
    store.nextFocus({ closeOnMobile: true })
  }, [store])

  return <>
    <p>{ i18nScoped.t('subtitle') }</p>

    <SelectableListComponent
      name="customerPlan"
      value={ customerPlan }
      data={ offeredService.customer_plans }
      formatter={ val => formatCustomerPlanName(val, theme, market, displayPriceWithTax) }
      onChange={ val => { store.set({ customerPlan: val }) } } />

    { !customerPlan && !customer && !window.currentUser && <p>
      <b>{ i18nScoped.t('not_logged_already_subscribed') }</b> { i18nScoped.t('not_logged_log_in_or_skip') }
    </p> }

    { customerPlan && <CustomerPlanDescriptionComponent customerPlan={ customerPlan } offeredServices={ offeredServices } /> }

    <PrimaryButton
      style={{ marginTop: '1em', display: 'block' }}
      onClick={ skipCustomerPlan }
      children={ i18nScoped.t('no_customer_plan') } />

    <PrimaryButton
      id="js-test-tooltip-next"
      style={{ marginTop: '0.5em', display: 'block' }}
      onClick={ () => customerPlan && store.nextFocus({ closeOnMobile: true }) }
      $disabled={ !customerPlan }
      children={ i18nScoped.t('next') } />
  </>
}

type FormatCustomerPlanNameData = Pick<StoreProps['data']['offeredService']['customer_plans'][0], 'name' | 'quantity_frequency' | 'quantity' | 'validity_duration_in_months' | 'upfront_payment' | 'price_with_tax_cents' | 'price_without_tax_cents' | 'offered_service_ids'>

export function formatCustomerPlanName<T extends FormatCustomerPlanNameData> (customerPlan: T, theme: DefaultTheme, market: Pick<SupplierBase, 'currency'>, displayPriceWithTax: boolean, firstOfferedService?: { name: string }) {
  const monthlyPrice = customerPlan[displayPriceWithTax ? 'price_with_tax_cents' : 'price_without_tax_cents'] / 100
  const totalPrice = monthlyPrice * customerPlan.validity_duration_in_months

  const offeredServicesText = customerPlan.offered_service_ids.length === 1 && firstOfferedService
    ? i18nScoped.t('on_one_offered_service', { service_name: firstOfferedService.name })
    : i18nScoped.t('on_offered_services', { count: customerPlan.offered_service_ids.length })

  return <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5em' }}>
    <div className="u-txtTruncate u-txtLeft">
      <div>{ customerPlan.name }</div>
      <div style={{ fontSize: '0.8em' }}>{ i18nScoped.t('hours_' + customerPlan.quantity_frequency, { count: customerPlan.quantity }) } { i18nScoped.t('on_months', { count: customerPlan.validity_duration_in_months }) }</div>
      <div style={{ fontSize: '0.8em' }}>{ offeredServicesText }</div>
      { customerPlan.upfront_payment && <div style={{ fontSize: '0.8em' }}>{ i18nScoped.t('upfront_payment', { total_price: currencyWrapTaxNotice(currencyFormat(market.currency, totalPrice.toFixed(2)), displayPriceWithTax) }) }</div> }
    </div>

    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
      <span style={{ backgroundColor: theme.colors.clr_1, color: 'white', fontWeight: 'bold', borderRadius: '1em', padding: '0.25em 0.5em' }}>
        { i18nScoped.t('price_per_month', { price: currencyWrapTaxNotice(currencyFormat(market.currency, monthlyPrice.toFixed(2)), displayPriceWithTax) }) }
      </span>
    </div>
  </div>
}

type FormatCustomerPlanNameAsStringData = Pick<StoreProps['data']['offeredService']['customer_plans'][0], 'name' | 'quantity_frequency' | 'quantity' | 'validity_duration_in_months'>

export function formatCustomerPlanNameAsString<T extends FormatCustomerPlanNameAsStringData> (customerPlan: T, withName = true) {
  // Fix mobile display by adding non-breaking spaces
  return [
    withName && `${customerPlan.name} :`, 
    i18nScoped.t('hours_' + customerPlan.quantity_frequency, { count: customerPlan.quantity }),
    i18nScoped.t('on_months', { count: customerPlan.validity_duration_in_months })
  ].filter(Boolean).join(' ').replace(/ /g, '\u00A0')
}
